import React from 'react';
import { Icon } from './icons';
import { Dialog } from './TodoPeople';
import { ColorPicker } from './MoreMenu';

/*
 * THE PRIVATE LIST (migration 089), the screen's side.
 *
 * Every member of staff has one list called Private, pinned under the Inbox
 * with a padlock. What is in it is theirs alone: the server shows nobody
 * else its to-dos, and this file keeps the screen honest about that -
 *
 *   the list's ⋯ offers only what Private allows (rename, colour, empty);
 *   a colleague looking through View as sees the list and a locked page,
 *   "Private - only Rita can see this", and nothing else;
 *   moving a to-do INTO Private asks, because whoever has it loses it (the
 *   assignee, the person who sent it, its links to other to-dos); moving one
 *   OUT asks too, because it stops being private;
 *   emptying it asks, since there is no undo.
 *
 * Nothing here talks to the server; TodoApp.js does the calls.
 */

export const isPrivateList = (l) => Boolean(l && l.is_private);

/** The owner's name for the locked page: the list's one member. */
const ownerOf = (list) => (list?.members || []).find((m) => m.role === 'owner')?.name
  || (list?.members || [])[0]?.name || null;

/** The ⋯ of the Private list: rename, colour, empty - never share, archive, delete or move. */
export const privateMenuItems = (list, { actions, onRename }) => [
  { label: 'Rename', icon: 'edit', onClick: onRename },
  { label: 'Colour', icon: 'tag', sub: (close) => (
    <ColorPicker name={list.name} value={list.color} onPick={(c) => { close(); actions.colorList(list, c); }} />) },
  { sep: true },
  { label: 'Empty Private…', icon: 'trash', danger: true, onClick: () => actions.emptyList(list) }
];

/** Under the header of my own Private list: one quiet line saying what it is. */
export function PrivateHint() {
  return (
    <p className="tda-private-hint" data-private-hint="">
      <Icon name="lock" size={13} />
      <span>Private — only you can see this list. Nothing in it can be shared, given to anybody or sent.</span>
    </p>
  );
}

/** A colleague's Private list, seen through View as: that it exists, and no more. */
export function PrivateHidden({ list }) {
  const who = ownerOf(list);
  return (
    <div className="ctd-empty ctd-empty-state tda-private-hidden" role="status" data-private-hidden={list.id}>
      <span className="ctd-empty-ic" aria-hidden="true"><Icon name="lock" size={28} /></span>
      <p className="ctd-empty-title">Private — only {who || 'its owner'} can see this</p>
      <p className="ctd-empty-text">
        This list is {who ? `${who.split(/\s+/)[0]}’s` : 'its owner’s'} alone. Nothing in it is shown here — not its
        to-dos, not how many there are.
      </p>
    </div>
  );
}

/**
 * Moving a to-do into or out of Private asks first. `into` is the Private
 * list it is going to, or null when it is leaving one for `to`.
 */
export function PrivateMoveDialog({ item, into, to, onConfirm, onClose }) {
  const title = item?.title ? `“${item.title}”` : 'this to-do';
  const held = item?.assignee_name && item.assignee_key !== item.owner_key ? item.assignee_name : null;
  const waiting = Array.isArray(item?.waiting_on) ? item.waiting_on.length : 0;
  const blocking = Number(item?.blocking_count) || 0;
  const losses = [];
  if (held) losses.push(`${held} will no longer have it`);
  if (item?.assigned_by_key && item.assigned_by_key !== item.owner_key && item.assigned_by_name) {
    losses.push(`${item.assigned_by_name}, who sent it, loses sight of it`);
  }
  if (waiting || blocking) losses.push('its links to other to-dos come off');
  return (
    <Dialog label={into ? `Move ${title} into Private?` : `Move ${title} out of Private?`} onClose={onClose}
            className="tda-private-ask">
      <div className="tda-private-ask-body" data-private-move={into ? 'in' : 'out'}>
        <p className="tda-private-ask-text">
          <Icon name="lock" size={16} />
          <span>
            {into
              ? `Only you will see it${losses.length ? `: ${losses.join(', ')}.` : '.'}`
              : `It stops being private: everybody on “${to?.name || 'that list'}” will see it.`}
          </span>
        </p>
        <div className="tda-private-ask-buttons">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Keep it where it is</button>
          <button type="button" className="btn btn-primary" autoFocus onClick={onConfirm}>
            {into ? 'Move into Private' : 'Move it out'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** Emptying a list (the one way to clear Private): every to-do in it goes, for good. */
export function EmptyListDialog({ list, count, onConfirm, onClose }) {
  const n = Number(count) || 0;
  return (
    <Dialog label={`Empty “${list.name}”?`} onClose={onClose} className="tda-private-ask">
      <div className="tda-private-ask-body" data-empty-list={list.id}>
        <p className="tda-private-ask-text">
          <Icon name="trash" size={16} />
          <span>
            {n ? `${n} ${n === 1 ? 'to-do goes' : 'to-dos go'}, done ones and notes included. ` : 'Everything in it goes. '}
            This cannot be undone. The list stays.
          </span>
        </p>
        <div className="tda-private-ask-buttons">
          <button type="button" className="btn btn-secondary" autoFocus onClick={onClose}>Keep them</button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>Empty it</button>
        </div>
      </div>
    </Dialog>
  );
}
